// Legacy: entry-dialog removeHtmltagsStylesAndClasses + sanitizeDescriptionHtml

import DOMPurify from 'isomorphic-dompurify';

export {
  injectPreviewOverflowOverrides,
  prepareResumePreviewDocument,
  sanitizeResumePreviewHtml,
} from './resume-preview-document';

/** Tags allowed in entry/summary description HTML (editor + list previews). */
export const ENTRY_DESCRIPTION_ALLOWED_TAGS = [
  'p',
  'br',
  'ul',
  'ol',
  'li',
  'b',
  'strong',
  'i',
  'em',
  'u',
  'span',
  'a',
];
export const ENTRY_DESCRIPTION_ALLOWED_ATTR = ['href', 'target', 'rel'];

/** Strip bold/italic tags while keeping text (legacy blocks B/I in editor). */
export function stripBoldItalicTags(html: string): string {
  if (!html) {
    return '';
  }
  return html
    .replace(/<\/?(?:b|strong|i|em)\b[^>]*>/gi, '')
    .replace(/style\s*=\s*["'][^"']*["']/gi, '')
    .replace(/class\s*=\s*["'][^"']*["']/gi, '');
}

export function sanitizeEntryDescriptionHtml(html: string): string {
  const stripped = stripBoldItalicTags(html || '');
  const purified = DOMPurify.sanitize(stripped, {
    ALLOWED_TAGS: ENTRY_DESCRIPTION_ALLOWED_TAGS,
    ALLOWED_ATTR: ENTRY_DESCRIPTION_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
  return normalizeListHtml(purified);
}

/**
 * Ensure list markup TipTap/ProseMirror accepts: wrap bare text in <p>,
 * ensure li children are block paragraphs, drop empty lists.
 */
export function normalizeListHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }
  if (typeof document === 'undefined') {
    // SSR: best-effort string normalize for common illegal shapes.
    return html
      .replace(/<li(\s[^>]*)?>\s*([^<][\s\S]*?)\s*<\/li>/gi, '<li$1><p>$2</p></li>')
      .replace(/<(ul|ol)(\s[^>]*)?>\s*<\/\1>/gi, '');
  }
  try {
    const template = document.createElement('template');
    template.innerHTML = html;
    const root = template.content;
    root.querySelectorAll('ul, ol').forEach((list) => {
      const items = Array.from(list.children).filter((child) => child.tagName === 'LI');
      if (items.length === 0) {
        list.remove();
        return;
      }
      items.forEach((li) => {
        const hasBlock = Array.from(li.childNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            ['P', 'UL', 'OL', 'DIV'].includes((node as Element).tagName),
        );
        if (!hasBlock) {
          const text = li.textContent || '';
          li.innerHTML = '';
          const p = document.createElement('p');
          p.textContent = text;
          li.appendChild(p);
        }
      });
    });
    const container = document.createElement('div');
    container.appendChild(root.cloneNode(true));
    return container.innerHTML;
  } catch {
    return html;
  }
}

/** Normalize pasted plain text like legacy onPaste. */
export function normalizePastedPlainText(text: string): string {
  let next = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  // Word-style bullets
  next = next.replace(/^[ \t]*[•·▪◦‣⁃]\s*/gm, '');
  next = next.replace(/\n{3,}/g, '\n\n');
  return next.trim();
}

export function isValidEntryUrl(url: string): boolean {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return true;
  }
  try {
    const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(withProtocol);
    return Boolean(parsed.hostname && parsed.hostname.includes('.'));
  } catch {
    return false;
  }
}
